import { makeStyles, tokens, Button, Text, Spinner } from '@fluentui/react-components';
import { ArrowRightFilled } from '@fluentui/react-icons';
import { MOBILE_BREAKPOINT } from './constants';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  wrapper: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingBlock: '60px',
    paddingInline: tokens.spacingHorizontalXXL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  card: {
    backgroundImage: ottoTokens.landingFinalCtaBackground,
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    paddingBlock: '80px',
    paddingInline: '48px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '56px',
      paddingInline: tokens.spacingHorizontalXL,
    },
  },
  title: {
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    maxWidth: '560px',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryCta: {
    backgroundImage: ottoTokens.landingPrimaryGradient,
    color: ottoTokens.landingPrimaryGradientForeground,
    border: '1px solid transparent',
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundImage: ottoTokens.landingPrimaryGradient,
      color: ottoTokens.landingPrimaryGradientForeground,
      transform: 'translateY(-1px)',
      boxShadow: tokens.shadow16,
    },
    ':hover:active': {
      backgroundImage: ottoTokens.landingPrimaryGradient,
      color: ottoTokens.landingPrimaryGradientForeground,
      transform: 'translateY(0)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
});

interface CtaSectionProps {
  onLogin: (source?: string) => void;
  /** True while MSAL is verifying the silent session. Disables the CTA
   *  and shows a spinner during the verification window. */
  isVerifyingAuth?: boolean;
}

export const CtaSection = ({ onLogin, isVerifyingAuth = false }: CtaSectionProps) => {
  const styles = useStyles();

  return (
    <section id="cta" className={styles.wrapper} aria-label="Call to action">
      <div className={styles.card}>
        <Text as="h2" className={styles.title}>
          Start building today.
        </Text>
        <Text className={styles.subtitle}>
          Logic Apps Automation is in preview. Sign in and build your first workflow today.
        </Text>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            size="large"
            icon={isVerifyingAuth ? <Spinner size="tiny" /> : <ArrowRightFilled />}
            iconPosition="after"
            onClick={() => onLogin('final-cta')}
            disabled={isVerifyingAuth}
            aria-busy={isVerifyingAuth}
            className={styles.primaryCta}
          >
            {isVerifyingAuth ? 'Verifying access…' : 'Get started'}
          </Button>
          {/* TODO(landing): "Talk to the team" target not yet defined.
              Render the secondary CTA once the contact form / sales URL
              exists. See PR description for full TODO list. */}
        </div>
      </div>
    </section>
  );
};
